import { useGlobal } from '@/context/GlobalContext';
import { usePageContent } from '@/hooks/usePageContent';
import { SampleProducts, ProductCard } from '@/components/SampleProducts';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import SEO, { serviceSchema } from '@/components/SEO';
import { 
  Calendar, Camera, HardDrive, Bike, ExternalLink, Cpu, Eye, Settings,
  Car, Truck, CheckCircle, ArrowRight, ChevronRight
} from 'lucide-react';

const MobilityImagingPage = () => {
  const { settings } = useGlobal();
  const { content } = usePageContent('mobility');

  // Get content with defaults
  const hero = content.hero || {};
  const expertise = content.expertise || {};
  const capabilities = content.capabilities || {};

  // Default values
  const defaults = {
    hero: {
      badge: "SOLUTIONS",
      headline: "Mobility Imaging Solutions",
      description: "Dashcams, e-mirrors, MDVRs & vehicle cameras — designed with automotive-grade expertise, manufactured through our trusted partner network.",
      badges: ["OEM/ODM", "Private Label", "Automotive-Grade"],
      background_image: "https://images.unsplash.com/photo-1694582862276-0afe82801621?w=1200&q=80"
    },
    expertise: {
      title: "Engineering Expertise You Can Trust",
      description: "We don't just assemble cameras — we understand the engineering behind them. Our team works with automotive-grade chipsets, optimizes image quality, and designs for the harsh realities of vehicle environments.",
      points: [
        { title: "Automotive-Grade Chipsets", description: "We work with proven SoCs designed for vehicle environments" },
        { title: "ISP & IQ Tuning", description: "Image signal processing optimization for real-world driving conditions" },
        { title: "Sensor Selection", description: "Sony STARVIS, OmniVision — matched to your requirements" },
        { title: "Certification Support", description: "CE, FCC, RoHS, E-Mark guidance and documentation" }
      ]
    },
    capabilities: {
      title: "What We Can Build",
      description: "These are our core capability areas. Contact us to discuss your specific requirements.",
      items: [
        {
          title: "Dashcams & Vehicle Cameras",
          description: "Front, rear, and cabin cameras for cars, trucks, and commercial vehicles. From basic recording to advanced ADAS integration.",
          features: ["1080p to 4K resolution", "HDR / WDR for varied lighting", "Wide angle lenses (120°-170°)", "Night vision capability"],
          for_whom: "Aftermarket brands, fleet operators, OEMs"
        },
        {
          title: "MDVR & AI Recorders",
          description: "Multi-channel mobile DVRs for fleet management with optional AI capabilities — driver monitoring, ADAS, incident detection.",
          features: ["4 to 8+ channel options", "4G/WiFi connectivity", "GPS tracking integration", "Cloud management ready"],
          for_whom: "Fleet operators, logistics, public transport"
        },
        {
          title: "e-Mirrors & Display Systems",
          description: "Electronic mirror replacements and integrated display systems. Improved visibility, reduced blind spots, modern aesthetics.",
          features: ["High-brightness displays", "Camera + display integration", "OEM-style fitment", "Multiple input support"],
          for_whom: "Vehicle manufacturers, aftermarket suppliers"
        },
        {
          title: "Motorcycle Systems",
          description: "Purpose-built dashcam systems for motorcycles and scooters. Compact, vibration-resistant, waterproof.",
          features: ["Dual channel (front + rear)", "IP67 waterproof", "Vibration-dampened design", "WiFi app connectivity"],
          for_whom: "Consumer brands, motorcycle accessory distributors"
        }
      ]
    }
  };

  const h = { ...defaults.hero, ...hero };
  const e = { ...defaults.expertise, ...expertise };
  const c = { ...defaults.capabilities, ...capabilities };

  const capIcons = [Camera, HardDrive, Eye, Bike];

  const workWithUs = [
    { title: 'OEM/ODM Development', description: 'You have specific requirements — we design and develop a product to match. From concept to production.', icon: Settings },
    { title: 'Private Label / White Label', description: 'Ready-to-brand products from our existing portfolio. Your branding, packaging, and certification support.', icon: CheckCircle },
    { title: 'Distribution Partnership', description: 'For distributors looking to add mobility imaging to their lineup. Competitive pricing, reliable supply.', icon: Truck },
  ];

  return (
    <div className="min-h-screen bg-[#1a1f2e]" data-testid="mobility-page">
      <SEO 
        title="Mobility Imaging Solutions - Dashcams, MDVRs, e-Mirrors"
        description={h.description}
        keywords="dashcam OEM, vehicle camera manufacturer, MDVR supplier, e-mirror system, motorcycle dashcam, fleet camera, automotive camera ODM, private label dashcam, Sony sensor, Sony STARVIS, automotive vehicle camera, wholesale dashcam, dashcam distributor"
        canonicalUrl="https://www.trinity-tw.com/mobility-imaging"
        ogImage={h.background_image}
        structuredData={serviceSchema('Mobility Imaging Solutions', h.description)}
      />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 relative">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(${h.background_image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1f2e]/30 via-[#1a1f2e]/50 to-[#1a1f2e]/60" />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#2a324a] border border-[#3d4758] text-sm text-slate-300 mb-6">
            {h.badge}
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 font-['Montserrat']">
            {h.headline}
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            {h.description}
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            {(h.badges || []).map((badge, idx) => (
              <span key={idx} className="px-4 py-2 rounded-full bg-transparent border border-[#3d4758] text-sm text-slate-400">
                {badge}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Our Expertise */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#141824]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6 font-['Montserrat']">
                {e.title}
              </h2>
              <p className="text-slate-400 mb-8">
                {e.description}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(e.points || defaults.expertise.points).map((item, idx) => (
                  <div key={idx} className="flex gap-3">
                    <ChevronRight className="w-5 h-5 text-[#8b5cf6] flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-white font-medium text-sm">{item.title}</div>
                      <div className="text-slate-500 text-xs">{item.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center">
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl bg-[#232a3b] border border-[#3d4758] p-6 text-center">
                  <Cpu className="w-8 h-8 text-[#8b5cf6] mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white font-['Montserrat']">30+</div>
                  <div className="text-slate-400 text-xs">Years in Imaging</div>
                </div>
                <div className="rounded-xl bg-[#232a3b] border border-[#3d4758] p-6 text-center">
                  <Car className="w-8 h-8 text-[#8b5cf6] mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white font-['Montserrat']">100+</div>
                  <div className="text-slate-400 text-xs">OEM/ODM Projects</div>
                </div>
                <div className="col-span-2 rounded-xl overflow-hidden border border-[#3d4758]">
                  <img 
                    src="https://images.unsplash.com/photo-1675868934032-0607db7734dd?w=400&q=80"
                    alt="Motorcycle dashcam"
                    className="w-full h-32 object-cover opacity-70"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Can Build */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4 font-['Montserrat']">
              {c.title}
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              {c.description}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {(c.items || defaults.capabilities.items).map((cap, idx) => {
              const Icon = capIcons[idx] || Camera;
              return (
                <div key={idx} className="rounded-2xl bg-[#232a3b] border border-[#3d4758] p-8 hover:border-[#5b3589] transition-colors">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-[#5b3589]/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-[#8b5cf6]" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white font-['Montserrat']">
                        {cap.title}
                      </h3>
                    </div>
                  </div>
                  <p className="text-slate-400 mb-4">{cap.description}</p>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {(cap.features || []).map((feature, fidx) => (
                      <div key={fidx} className="flex items-center gap-2 text-sm text-slate-300">
                        <CheckCircle className="w-4 h-4 text-[#5b3589]" />
                        {feature}
                      </div>
                    ))}
                  </div>
                  <div className="pt-4 border-t border-[#3d4758]">
                    <span className="text-slate-500 text-xs">For: {cap.for_whom}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sample Camera Modules */}
      <SampleProducts
        endpoint="camera-modules"
        title="Sample Camera Modules"
        subtitle="Representative examples from our portfolio. Custom configurations available."
        limit={4}
        renderCard={(item) => (
          <ProductCard
            key={item.id}
            image={item.main_image}
            title={item.name}
            description={item.short_description}
            tags={item.key_features?.split('\n').slice(0, 2) || []}
            Icon={Camera}
          />
        )}
      />

      {/* Sample MDVR Recorders */}
      <SampleProducts
        endpoint="mdvr-recorders"
        title="Sample MDVR & AI Recorders"
        subtitle="Multi-channel recording solutions for fleet management and commercial vehicles."
        limit={4}
        renderCard={(item) => (
          <ProductCard
            key={item.id}
            image={item.main_image}
            title={item.model_name}
            description={item.short_description}
            tags={[item.channel_count, item.use_cases].filter(Boolean)}
            Icon={HardDrive}
          />
        )}
      />

      {/* How to Work With Us */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#141824]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4 font-['Montserrat']">
              How to Work With Us
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Whether you need full custom development or ready-to-brand products — we're flexible.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {workWithUs.map((option, idx) => (
              <div key={idx} className="rounded-2xl bg-[#232a3b] border border-[#3d4758] p-8">
                <div className="w-12 h-12 rounded-xl bg-[#5b3589]/20 flex items-center justify-center mb-4">
                  <option.icon className="w-6 h-6 text-[#8b5cf6]" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3 font-['Montserrat']">
                  {option.title}
                </h3>
                <p className="text-slate-400 text-sm">{option.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Motorcycle Brand Callout */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-2xl bg-gradient-to-r from-[#5b3589]/20 to-[#232a3b] border border-[#3d4758] p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 rounded-xl bg-[#5b3589]/30 flex items-center justify-center">
                  <Bike className="w-8 h-8 text-[#8b5cf6]" />
                </div>
                <div>
                  <div className="text-xs text-[#8b5cf6] font-medium mb-1">OUR CONSUMER BRAND</div>
                  <h3 className="text-2xl font-bold text-white font-['Montserrat']">
                    Anchilex Motorcycle Dashcams
                  </h3>
                  <p className="text-slate-400 text-sm">
                    Looking for ready-to-sell motorcycle recording solutions? Visit our consumer brand.
                  </p>
                </div>
              </div>
              <a 
                href="https://www.anchilex.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-[#1a1f2e] px-6 py-3 rounded-full font-medium hover:bg-slate-100 transition-colors"
              >
                Visit Anchilex.com
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#141824]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4 font-['Montserrat']">
            Let's Discuss Your Project
          </h2>
          <p className="text-slate-400 mb-8">
            Tell us about your requirements — product type, quantities, timeline. 
            We'll come back with the best approach.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={settings.teams_scheduling_url} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#5b3589] hover:bg-[#6d3d9e] text-white rounded-full px-8">
                <Calendar className="w-4 h-4 mr-2" />
                Schedule a Call
              </Button>
            </a>
            <Link to="/contact">
              <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 rounded-full px-8">
                Send an Inquiry
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MobilityImagingPage;
